import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Button, message, Tabs, Tag, Space, Tooltip, Empty, Spin } from 'antd';
import { CopyOutlined, DeleteOutlined, EyeOutlined, ClockCircleOutlined, UserOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import auctionService from '../../services/auctionService';
import moment from 'moment';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import env from '../../env';

const { Text } = Typography;
const { TabPane } = Tabs;

const SentInvitations = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'all';
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const navigate = useNavigate();

    useEffect(() => {
        loadInvitations();
    }, []);

    useEffect(() => {
        const tabFromUrl = searchParams.get('tab') || 'all';
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    const loadInvitations = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getSentInvitations();
            console.log('Loaded auctions:', response.data);
            setAuctions(response.data);
        } catch (error) {
            console.error('Error loading invitations:', error);
            message.error('Failed to load invitations');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = (code) => {
        const link = `${env.FRONTEND_URL}/auctions/invitation/${code}`;
        navigator.clipboard.writeText(link);
        message.success('Invitation link copied to clipboard');
    };

    const handleRemoveInvitation = async (invitationId) => {
        try {
            await auctionService.removeInvitation(invitationId);
            message.success('Invitation removed successfully');
            loadInvitations();
        } catch (error) {
            message.error('Failed to remove invitation');
        }
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            invited: { color: 'gold', text: 'Pending' },
            direct_link: { color: 'purple', text: 'Direct Link' },
            accepted: { color: 'success', text: 'Accepted' },
            declined: { color: 'error', text: 'Declined' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getAuctionStatusTag = (status) => {
        const statusConfig = {
            draft: { color: 'default', text: 'Draft' },
            scheduled: { color: 'blue', text: 'Scheduled' },
            active: { color: 'green', text: 'Active' },
            completed: { color: 'purple', text: 'Completed' },
            cancelled: { color: 'red', text: 'Cancelled' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const filterInvitations = (auction) => {
        if (activeTab === 'all') return auction.invitations;
        if (activeTab === 'pending') return auction.invitations.filter(inv => inv.status === 'invited');
        if (activeTab === 'accepted') return auction.invitations.filter(inv => inv.status === 'accepted');
        if (activeTab === 'direct') return auction.invitations.filter(inv => inv.status === 'direct_link');
        return auction.invitations;
    };

    const renderInvitationList = (auction) => {
        const filteredInvitations = filterInvitations(auction);
        
        if (filteredInvitations.length === 0) {
            return (
                <Empty 
                    description={`No ${activeTab === 'all' ? '' : activeTab} invitations for this auction`}
                    style={{ margin: '20px 0' }}
                />
            );
        }

        return (
            <List
                dataSource={filteredInvitations}
                renderItem={invitation => (
                    <List.Item
                        actions={[
                            <Tooltip title="Copy Invitation Link">
                                <Button 
                                    type="text" 
                                    icon={<CopyOutlined />} 
                                    onClick={() => handleCopyLink(invitation.invitation_code)}
                                />
                            </Tooltip>,
                            <Tooltip title="Remove Invitation">
                                <Button 
                                    type="text" 
                                    danger 
                                    icon={<DeleteOutlined />} 
                                    onClick={() => handleRemoveInvitation(invitation.id)}
                                />
                            </Tooltip>
                        ]}
                    >
                        <List.Item.Meta
                            title={
                                <Space>
                                    {invitation.first_name} {invitation.last_name}
                                    {getStatusTag(invitation.status)}
                                </Space>
                            }
                            description={
                                <Space direction="vertical" size="small">
                                    <Text type="secondary">
                                        {invitation.email}
                                    </Text>
                                    <Space>
                                        <Text type="secondary">
                                            <ClockCircleOutlined /> Invited: {moment(invitation.invited_at).format('MMM D, YYYY h:mm A')}
                                        </Text>
                                        {invitation.responded_at && (
                                            <Text type="secondary">
                                                • Responded: {moment(invitation.responded_at).format('MMM D, YYYY h:mm A')}
                                            </Text>
                                        )}
                                    </Space>
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };


    return (
        <DashboardLayout>
            <Spin spinning={loading}>
                <PageHeader
                    title="Sent Invitations"
                    description="Track all invitations you've sent for your private auctions. View who has accepted, declined, or is still pending. You can also manage direct invitation links here."
                />
                <div className="sent-invitations">
                    <Tabs 
                        activeKey={activeTab} 
                        onChange={(key) => {
                            setActiveTab(key);
                            setSearchParams({ tab: key });
                        }}
                        style={{ marginBottom: 24 }}
                    >
                        <TabPane tab="All Invitations" key="all" />
                        <TabPane tab="Pending" key="pending" />
                        <TabPane tab="Accepted" key="accepted" />
                        <TabPane tab="Direct Links" key="direct" />
                    </Tabs>

                    {auctions.length === 0 ? (
                        <Empty 
                            description="No invitations sent yet"
                            style={{ margin: '50px 0' }}
                        />
                    ) : (
                        <List
                            dataSource={auctions}
                            renderItem={auction => (
                                <Card 
                                    title={
                                        <Space>
                                            {auction.title}
                                            {getAuctionStatusTag(auction.status)}
                                        </Space>
                                    }
                                    extra={
                                        <Button 
                                            type="primary" 
                                            icon={<EyeOutlined />}
                                            onClick={() => navigate(`/auctions/${auction.slug}`)}
                                        >
                                            View Auction
                                        </Button>
                                    }
                                    style={{ marginBottom: 16 }}
                                >
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Space>
                                            <Tag color="blue">{auction.category.name}</Tag>
                                            <Text type="secondary">
                                                <ClockCircleOutlined /> Starts: {moment(auction.start_time).format('MMM D, YYYY h:mm A')}
                                            </Text>
                                        </Space>
                                        {renderInvitationList(auction)}
                                    </Space>
                                </Card>
                            )}
                        />
                    )}
                </div>
            </Spin>
        </DashboardLayout>
    );
};

export default SentInvitations; 